import React, { useState } from 'react';
import toast from 'react-hot-toast';
import type { SchemaField } from '../../utils/schemaForm';
import { readFileAsDataURI } from '../../utils/generation';

// Generalized from GenerationPanel's audio drag-drop affordance: same 5 MB
// cap, same "data URI replaces the typed value" behavior, now available on
// every url-widget field (accept type driven by the field's own `accept`).
const MAX_FILE_BYTES = 5 * 1024 * 1024;

interface SchemaFieldsProps {
  fields: SchemaField[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}

function FieldLabel({ field }: { field: SchemaField }) {
  return (
    <label className="mb-1 block text-xs font-medium text-gray-400" htmlFor={`schema-${field.name}`}>
      {field.label}
      {field.required ? ' *' : ''}
    </label>
  );
}

function FieldDescription({ field }: { field: SchemaField }) {
  if (!field.description) return null;
  return <p className="mb-1 text-[11px] text-gray-500">{field.description}</p>;
}

// Controlled dynamic form renderer for a parsed fal input schema. Owns only
// per-field UI-local state that isn't part of the submitted value itself
// (drag-drop filenames, the chips draft-input text) - the actual field
// values all live in the caller's `values` map, round-tripped through
// onChange, so GenerationPanel stays the single source of truth for what
// gets submitted.
export default function SchemaFields({ fields, values, onChange }: SchemaFieldsProps) {
  const [filenames, setFilenames] = useState<Record<string, string>>({});
  const [chipDrafts, setChipDrafts] = useState<Record<string, string>>({});

  const loadFile = async (field: SchemaField, file: File) => {
    try {
      const dataUri = await readFileAsDataURI(file, MAX_FILE_BYTES);
      onChange(field.name, dataUri);
      setFilenames((f) => ({ ...f, [field.name]: file.name }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to read file');
    }
  };

  const clearFile = (field: SchemaField) => {
    setFilenames((f) => {
      const next = { ...f };
      delete next[field.name];
      return next;
    });
    onChange(field.name, '');
  };

  return (
    <>
      {fields.map((field) => {
        const value = values[field.name];

        switch (field.widget) {
          case 'textarea':
            return (
              <div key={field.name}>
                <FieldLabel field={field} />
                <FieldDescription field={field} />
                <textarea
                  id={`schema-${field.name}`}
                  className="input"
                  rows={3}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => onChange(field.name, e.target.value)}
                />
              </div>
            );

          case 'text':
            return (
              <div key={field.name}>
                <FieldLabel field={field} />
                <FieldDescription field={field} />
                <input
                  id={`schema-${field.name}`}
                  type="text"
                  className="input"
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => onChange(field.name, e.target.value)}
                />
              </div>
            );

          case 'url': {
            const filename = filenames[field.name];
            return (
              <div key={field.name}>
                <FieldLabel field={field} />
                <FieldDescription field={field} />
                <input
                  id={`schema-${field.name}`}
                  type="url"
                  className="input"
                  // Display state is split from the submitted value: once a
                  // file is attached, `value` holds the (potentially huge)
                  // base64 data URI - showing that raw string in a visible
                  // input would leak it into the UI, so the filename is
                  // shown instead (the field is disabled while attached, so
                  // there's nothing to edit here anyway).
                  value={filename ? filename : typeof value === 'string' ? value : ''}
                  onChange={(e) => onChange(field.name, e.target.value)}
                  disabled={Boolean(filename)}
                />
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file) void loadFile(field, file);
                  }}
                  className="mt-2 rounded-lg border border-dashed border-gray-700 p-3 text-center text-xs text-gray-500"
                >
                  {filename ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-gray-300">{filename}</span>
                      <button
                        type="button"
                        onClick={() => clearFile(field)}
                        aria-label={`Clear selected file for ${field.label}`}
                        className="text-gray-500 hover:text-white"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      or drop a file - max 5 MB
                      <input
                        type="file"
                        accept={field.accept}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) void loadFile(field, file);
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            );
          }

          case 'number':
          case 'integer':
            return (
              <div key={field.name}>
                <FieldLabel field={field} />
                <FieldDescription field={field} />
                <input
                  id={`schema-${field.name}`}
                  type="number"
                  className="input"
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={value === undefined || value === null ? '' : (value as number | string)}
                  onChange={(e) => onChange(field.name, e.target.value)}
                />
              </div>
            );

          case 'select':
            return (
              <div key={field.name}>
                <FieldLabel field={field} />
                <FieldDescription field={field} />
                <select
                  id={`schema-${field.name}`}
                  className="input"
                  value={typeof value === 'string' ? value : ''}
                  onChange={(e) => onChange(field.name, e.target.value)}
                >
                  {!field.required && <option value="">-</option>}
                  {(field.enumValues ?? []).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
            );

          case 'boolean':
            return (
              <div key={field.name} className="flex items-center gap-2">
                <input
                  id={`schema-${field.name}`}
                  type="checkbox"
                  checked={Boolean(value)}
                  onChange={(e) => onChange(field.name, e.target.checked)}
                />
                <label htmlFor={`schema-${field.name}`} className="text-xs font-medium text-gray-400">
                  {field.label}
                  {field.required ? ' *' : ''}
                </label>
                {field.description && <span className="text-[11px] text-gray-500">{field.description}</span>}
              </div>
            );

          case 'chips': {
            const chipValues = Array.isArray(value) ? (value as string[]) : [];
            const draft = chipDrafts[field.name] ?? '';
            const commit = () => {
              const tag = draft.trim().replace(/,+$/, '');
              if (tag && !chipValues.includes(tag)) onChange(field.name, [...chipValues, tag]);
              setChipDrafts((d) => ({ ...d, [field.name]: '' }));
            };
            return (
              <div key={field.name}>
                <FieldLabel field={field} />
                <FieldDescription field={field} />
                <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-700 bg-gray-800 px-2 py-1.5 focus-within:border-primary-500">
                  {chipValues.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-gray-700 px-2 py-0.5 text-xs text-gray-200"
                    >
                      {tag}
                      <button
                        type="button"
                        aria-label={`Remove ${tag}`}
                        className="text-gray-400 hover:text-white"
                        onClick={() => onChange(field.name, chipValues.filter((t) => t !== tag))}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    id={`schema-${field.name}`}
                    className="min-w-[8rem] flex-1 bg-transparent text-sm text-white outline-none placeholder-gray-500"
                    value={draft}
                    placeholder={chipValues.length === 0 ? 'Add and press Enter' : undefined}
                    onChange={(e) => setChipDrafts((d) => ({ ...d, [field.name]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        commit();
                      } else if (e.key === 'Backspace' && draft === '' && chipValues.length > 0) {
                        onChange(field.name, chipValues.slice(0, -1));
                      }
                    }}
                    onBlur={commit}
                  />
                </div>
              </div>
            );
          }

          case 'json': {
            const raw = typeof value === 'string' ? value : '';
            let error: string | null = null;
            if (raw.trim() !== '') {
              try {
                JSON.parse(raw);
              } catch {
                error = 'Invalid JSON';
              }
            }
            return (
              <details key={field.name}>
                <summary className="cursor-pointer select-none text-xs font-medium text-gray-400">
                  {field.label}
                  {field.required ? ' *' : ''}
                </summary>
                <FieldDescription field={field} />
                <textarea
                  className="input mt-2 font-mono text-xs"
                  rows={3}
                  value={raw}
                  onChange={(e) => onChange(field.name, e.target.value)}
                  aria-invalid={error !== null}
                  aria-label={field.label}
                />
                {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
              </details>
            );
          }

          default:
            return null;
        }
      })}
    </>
  );
}
